import type { LabelHTMLAttributes } from 'react';
import { cx } from './cx.js';
import { FieldLabel } from './FieldLabel.js';
import { Slider } from './Slider.js';

/**
 * A setting the player sets by amount rather than by answer: how loud.
 *
 * The name is the point. `Switch` says on or off, `Choice` says pick one, and
 * this says how much — all three name the decision, none names the widget. That
 * leaves the widget name free for the one file that draws the widget, which is
 * what it was called before: this component was `Slider`, `ui/Slider.tsx` was
 * also `Slider`, and this file imported that one under the alias `SliderInput`
 * to keep them apart. An alias is a name collision with the argument left out.
 *
 * The chip thumb in its groove is `Slider`, and it is only ever that. The label
 * above it, the line of its own it sits on, and the percent read out beside it
 * are this.
 *
 * Two things it supplies that a caller would forget. The track gets its own
 * line under the label, because a track squeezed beside a label is a track no
 * thumb can hit. And the value is stored 0 to 1 but shown and driven 0 to 100:
 * a player thinks in percent, and a range input stepping in hundredths of
 * nothing is a range input that fights back. Both conversions live here so no
 * screen does the arithmetic twice.
 */

export interface LevelProps extends Omit<
  LabelHTMLAttributes<HTMLLabelElement>,
  'className' | 'style' | 'children' | 'onChange'
> {
  /** What the setting is called. Already translated. */
  readonly label: string;
  /** Stored as a fraction, 0 to 1. */
  readonly value: number;
  /** Muted, or otherwise not something to set right now. */
  readonly disabled: boolean;
  /** Called with a fraction, 0 to 1. */
  readonly onChange: (next: number) => void;
  /** Layout and state only. The row and the track are not yours. */
  readonly className?: string | undefined;
}

const ROW = 'flex min-h-13 flex-wrap items-center gap-x-4 gap-y-1 py-2 text-ink';

export function Level({
  label,
  value,
  disabled,
  onChange,
  className,
  ...rest
}: LevelProps): React.JSX.Element {
  const percent = Math.round(value * 100);
  return (
    <label className={cx(ROW, className)} {...rest}>
      <FieldLabel className="grow basis-3/5">{label}</FieldLabel>
      <Slider
        className="order-3 grow basis-full"
        min={0}
        max={100}
        step={5}
        value={percent}
        disabled={disabled}
        onChange={(event) => {
          onChange(Number(event.target.value) / 100);
        }}
      />
      <output className="ml-auto min-w-11 text-right font-display text-sm text-gray-700 tabular-nums">{`${String(percent)}%`}</output>
    </label>
  );
}
